import React from 'react';
import PropTypes from 'prop-types';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Heading2,
  Quote,
  Code,
} from 'lucide-react';

/**
 * MenuBar - Formatting toolbar for rich text editor
 */
const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const Button = ({ onClick, isActive, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      className={`
        p-2 rounded transition-colors
        ${
          isActive
            ? 'bg-primary-100 text-primary-700'
            : 'text-neutral-700 hover:bg-neutral-100'
        }
      `}
      title={title}
    >
      {children}
    </button>
  );

  return (
    <div className="border-b border-neutral-200 p-2 flex flex-wrap gap-1">
      <Button
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold (Ctrl+B)"
      >
        <Bold className="w-5 h-5" />
      </Button>

      <Button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic (Ctrl+I)"
      >
        <Italic className="w-5 h-5" />
      </Button>

      <div className="w-px bg-neutral-300 mx-1" />

      <Button
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive('heading', { level: 2 })}
        title="Heading"
      >
        <Heading2 className="w-5 h-5" />
      </Button>

      <div className="w-px bg-neutral-300 mx-1" />

      <Button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet List"
      >
        <List className="w-5 h-5" />
      </Button>

      <Button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered List"
      >
        <ListOrdered className="w-5 h-5" />
      </Button>

      <div className="w-px bg-neutral-300 mx-1" />

      <Button
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Quote"
      >
        <Quote className="w-5 h-5" />
      </Button>

      <Button
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        title="Code Block"
      >
        <Code className="w-5 h-5" />
      </Button>

      <div className="w-px bg-neutral-300 mx-1" />

      <Button onClick={setLink} isActive={editor.isActive('link')} title="Add Link">
        <LinkIcon className="w-5 h-5" />
      </Button>
    </div>
  );
};

MenuBar.propTypes = {
  editor: PropTypes.object,
};

/**
 * RichTextEditor - Web implementation using Tiptap
 *
 * @param {string} value - HTML content
 * @param {function} onChangeText - Callback with HTML content
 * @param {string} placeholder - Placeholder text
 * @param {string} error - Error message
 * @param {boolean} disabled - Whether editor is disabled
 * @param {number} maxLength - Optional character limit (counts plain text)
 */
const RichTextEditor = ({
  value,
  onChangeText,
  placeholder = 'Start typing...',
  error,
  disabled = false,
  label,
  required = false,
  className = '',
  maxLength = null,
}) => {
  const [charCount, setCharCount] = React.useState(0);

  const getPlainTextLength = (html) => {
    if (!html) return 0;
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim().length;
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary-600 underline hover:text-primary-700',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (html === '<p></p>') {
        onChangeText('');
        setCharCount(0);
      } else {
        onChangeText(html);
        setCharCount(getPlainTextLength(html));
      }
    },
  });

  // Update editor when value changes externally
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
      setCharCount(getPlainTextLength(value));
    }
  }, [value, editor]);

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="label">
          {label}
          {required && <span className="text-error-600 ml-1">*</span>}
        </label>
      )}

      <div
        className={`
          border rounded-lg overflow-hidden bg-white
          ${error ? 'border-error-600' : 'border-neutral-300'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <MenuBar editor={editor} />

        <EditorContent
          editor={editor}
          className={`
            prose prose-sm max-w-none p-4
            min-h-[200px]
            focus-within:outline-none
            ${disabled ? 'pointer-events-none' : ''}
          `}
        />
      </div>

      <div className="flex justify-between items-start mt-1">
        {error
          ? <p className="text-sm text-error-600">{error}</p>
          : <span />
        }
        {maxLength != null && (
          <p className={`text-xs ml-2 shrink-0 ${charCount > maxLength ? 'text-error-600' : 'text-neutral-500'}`}>
            {charCount.toLocaleString()}/{maxLength.toLocaleString()} characters
          </p>
        )}
      </div>
    </div>
  );
};

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChangeText: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  maxLength: PropTypes.number,
};

export default RichTextEditor;
